import { cva, type VariantProps } from "class-variance-authority";
import classNames from "components/ui/classNames";
import * as React from "react";

const badgeVariants = cva(
  classNames(
    "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold",
    "focus:ring-ring focus:outline-none focus:ring-2 focus:ring-offset-2",
  ),
  {
    variants: {
      variant: {
        default: "border-transparent bg-slate-100 text-slate-700 dark:bg-slate-400/30 dark:text-slate-200",
        chestnut: "bg-chestnut-100 text-chestnut-700 border-transparent dark:bg-chestnut-400/30 dark:text-chestnut-200",
        red: "bg-red-100 text-red-700 border-transparent dark:bg-red-400/30 dark:text-red-200",
        orange: "bg-orange-100 text-orange-700 border-transparent dark:bg-orange-400/30 dark:text-orange-200",
        amber: "bg-amber-100 text-amber-700 border-transparent dark:bg-amber-400/30 dark:text-amber-200",
        yellow: "bg-yellow-100 text-yellow-700 border-transparent dark:bg-yellow-400/30 dark:text-yellow-200",
        lime: "bg-lime-100 text-lime-700 border-transparent dark:bg-lime-400/30 dark:text-lime-200",
        green: "bg-green-100 text-green-700 border-transparent dark:bg-green-400/30 dark:text-green-200",
        emerald: "bg-emerald-100 text-emerald-700 border-transparent dark:bg-emerald-400/30 dark:text-emerald-200",
        teal: "bg-teal-100 text-teal-700 border-transparent dark:bg-teal-400/30 dark:text-teal-200",
        cyan: "bg-cyan-100 text-cyan-700 border-transparent dark:bg-cyan-400/30 dark:text-cyan-200",
        sky: "bg-sky-100 text-sky-700 border-transparent dark:bg-sky-400/30 dark:text-sky-200",
        blue: "bg-blue-100 text-blue-700 border-transparent dark:bg-blue-400/30 dark:text-blue-200",
        indigo: "bg-indigo-100 text-indigo-700 border-transparent dark:bg-indigo-400/30 dark:text-indigo-200",
        violet: "bg-violet-100 text-violet-700 border-transparent dark:bg-violet-400/30 dark:text-violet-200",
        purple: "bg-purple-100 text-purple-700 border-transparent dark:bg-purple-400/30 dark:text-purple-200",
        fuchsia: "bg-fuchsia-100 text-fuchsia-700 border-transparent dark:bg-fuchsia-400/30 dark:text-fuchsia-200",
        pink: "bg-pink-100 text-pink-700 border-transparent dark:bg-pink-400/30 dark:text-pink-200",
        rose: "bg-rose-100 text-rose-700 border-transparent dark:bg-rose-400/30 dark:text-rose-200",
        olive: "bg-olive-100 text-olive-700 border-transparent dark:bg-olive-400/30 dark:text-olive-200",
        viridian: "bg-viridian-100 text-viridian-700 border-transparent dark:bg-viridian-400/30 dark:text-viridian-200",
        navy: "bg-navy-100 text-navy-700 border-transparent dark:bg-navy-400/30 dark:text-navy-200",
        sea: "bg-sea-100 text-sea-700 border-transparent dark:bg-sea-400/30 dark:text-sea-200",
        slate: "bg-slate-200 text-slate-700 border-transparent dark:bg-slate-400/30 dark:text-slate-200",
        zinc: "bg-zinc-100 text-zinc-700 border-transparent dark:bg-zinc-600 dark:text-zinc-50",
        gray: "bg-gray-100 text-gray-700 border-transparent dark:bg-gray-400/30 dark:text-gray-200",
        neutral: "bg-neutral-200 text-neutral-700 border-transparent dark:bg-neutral-400/30 dark:text-neutral-200",
        brand: "bg-brand/10 text-brand border-transparent dark:bg-brand/10 dark:text-brand",
        disabled: "bg-neutral-100 text-neutral-400 border-transparent dark:bg-neutral-400/30 dark:text-neutral-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef(({ className, variant, ...props }: BadgeProps, ref: React.Ref<HTMLDivElement>) => {
  return <div ref={ref} className={classNames(badgeVariants({ variant }), className)} {...props} />;
});

export { Badge, badgeVariants };
